import { Check, Copy, Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { clients as clientsApi } from "./api";

/**
 * CopyButton — copies `value` to the clipboard and flashes a check for ~1.5s.
 * Used next to revealed secrets (SSN, DOB, monitoring + Credit Karma logins)
 * so an operator can grab a password without hand-retyping it. Renders nothing
 * when there is no value to copy.
 */
export function CopyButton({ value, title = "Copy" }: { value?: string | null; title?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  if (!value) return null;
  async function copy() {
    let ok = false;
    try {
      await navigator.clipboard.writeText(value as string);
      ok = true;
    } catch {
      // Fallback for contexts where the async clipboard API is unavailable.
      // The temporary textarea holds the secret, so always remove it, even if
      // selection or execCommand throws.
      const ta = document.createElement("textarea");
      try {
        ta.value = value as string;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy") === true;
      } catch {
        ok = false;
      } finally {
        if (ta.parentNode) ta.parentNode.removeChild(ta);
      }
    }
    if (!ok) return;
    setCopied(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button
      type="button"
      className="revealable-toggle"
      onClick={copy}
      title={copied ? "Copied" : title}
      aria-label={copied ? "Copied" : title}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

interface RevealableFieldProps {
  clientId: string;
  field: "ssn" | "dob";
  maskedValue?: string | null;
  // How long the value stays revealed before auto-hiding.
  autoHideMs?: number;
  className?: string;
}

/**
 * RevealableField — renders the masked value plus an eye icon.
 * Click the icon → calls /api/clients/:id/reveal which:
 *   - decrypts the value server-side
 *   - writes an events row of kind='pii.revealed'
 *   - returns the cleartext value
 * Auto-hides after 30s or when the host element loses focus.
 */
export function RevealableField({
  clientId,
  field,
  maskedValue,
  autoHideMs = 30_000,
  className,
}: RevealableFieldProps) {
  // The revealed value is tagged with the client id it belongs to, so it can
  // NEVER render against a different client — even for the render between a
  // client switch and the reset effect firing. `shown` is the render-time gate.
  const [revealed, setRevealed] = useState<{ forId: string; value: string } | null>(null);
  const shown = revealed && revealed.forId === clientId ? revealed.value : null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  // Always the currently-rendered client id — used to drop a reveal response
  // that resolves after the host switched to a different client (these field
  // instances are reused across ClientDetail routes).
  const idRef = useRef(clientId);
  idRef.current = clientId;

  const fallbackMask = field === "ssn" ? "XXX-XX-••••" : "XX/XX/••••";
  const masked = maskedValue || fallbackMask;

  function hide() {
    setRevealed(null);
    setError(null);
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  async function reveal() {
    if (shown) {
      hide();
      return;
    }
    const requestedId = clientId;
    setLoading(true);
    setError(null);
    try {
      const res = await clientsApi.revealPii(clientId, field);
      if (idRef.current !== requestedId) return; // client changed mid-flight — discard
      setRevealed({ forId: requestedId, value: res.value });
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(hide, autoHideMs);
    } catch (e: any) {
      if (idRef.current !== requestedId) return;
      setError(e?.message || "Could not reveal");
    } finally {
      if (idRef.current === requestedId) setLoading(false);
    }
  }

  // Clear any revealed value + in-flight flags the instant the host switches
  // clients, so a revealed SSN/DOB can never linger on another client's page.
  useEffect(() => { hide(); setLoading(false); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <span
      className={`revealable-field ${className || ""}`}
      onBlur={(e) => {
        // Hide only when focus leaves the whole field — not when it moves from
        // the eye toggle to the copy button (that blur must not unmount copy
        // before its click lands).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hide();
      }}
      tabIndex={-1}
    >
      <span className="revealable-value">{shown || masked}</span>
      <button
        type="button"
        className="revealable-toggle"
        onClick={reveal}
        title={shown ? "Hide" : `Reveal ${field.toUpperCase()}`}
        disabled={loading}
      >
        {loading ? (
          <Loader2 size={14} className="spin" />
        ) : shown ? (
          <EyeOff size={14} />
        ) : (
          <Eye size={14} />
        )}
      </button>
      {shown && <CopyButton value={shown} title={`Copy ${field.toUpperCase()}`} />}
      {error && <span className="revealable-error">{error}</span>}
    </span>
  );
}
